import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Globe, Server, FolderTree, Link2, Route, Archive,
  Activity, ScrollText, Users, SlidersHorizontal, Settings, Command,
  LogOut, Search, Circle, Cloud, Network, Menu, X, Terminal, ShieldQuestion,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CommandPalette } from "@/components/CommandPalette";
import ConnectionStatus from "@/components/ConnectionStatus";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/sites", label: "Sites", icon: Globe },
  { to: "/apps", label: "Apps", icon: Server },
  { to: "/files", label: "Files", icon: FolderTree },
  { to: "/links", label: "Links", icon: Link2 },
  { to: "/cloudrun", label: "Cloud Run", icon: Cloud },
  { to: "/aws", label: "AWS Hosting", icon: Cloud },
  { to: "/freedns", label: "FreeDNS", icon: Network },
  { to: "/lightspeed", label: "Lightspeed Checker", icon: ShieldQuestion },
  { to: "/routing", label: "Routing", icon: Route },
  { to: "/backups", label: "Backups", icon: Archive },
  { to: "/monitoring", label: "Monitoring", icon: Activity },
  { to: "/logs", label: "Logs", icon: ScrollText },
  { to: "/users", label: "Users", icon: Users },
  { to: "/server", label: "Server Control", icon: SlidersHorizontal },
  { to: "/console", label: "Console", icon: Terminal },
  { to: "/settings", label: "Settings", icon: Settings },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") setMobileOpen(false);
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100">
      <CommandPalette open={paletteOpen} setOpen={setPaletteOpen} />

      {/* Sidebar */}
      <button aria-label="Open navigation" aria-expanded={mobileOpen} onClick={()=>setMobileOpen(true)} className="lg:hidden fixed top-3 right-3 z-20 rounded-md border border-zinc-700 bg-zinc-900 p-2"><Menu className="h-5 w-5"/></button>
      {mobileOpen&&<button className="lg:hidden fixed inset-0 z-20 bg-black/60" aria-label="Close navigation overlay" onClick={()=>setMobileOpen(false)}/>}
      <aside className={`fixed inset-y-0 left-0 z-30 w-60 border-r border-zinc-800 bg-[#0c0c0e] flex flex-col transition-transform ${mobileOpen?'translate-x-0':'-translate-x-full'} lg:translate-x-0`}>
        <div className="flex items-center gap-2 px-5 h-14 border-b border-zinc-800">
          <div className="h-6 w-6 rounded bg-zinc-100 flex items-center justify-center">
            <Circle className="h-3.5 w-3.5 text-zinc-900 fill-zinc-900" />
          </div>
          <span className="font-head font-semibold tracking-tight">Cannon</span>
          <span className="text-[10px] font-mono text-zinc-500 border border-zinc-800 rounded px-1 ml-auto">v6</span>
          <button aria-label="Close navigation" onClick={()=>setMobileOpen(false)} className="lg:hidden p-1"><X className="h-4 w-4"/></button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={()=>setMobileOpen(false)}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                cn("flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors duration-75",
                  isActive ? "bg-zinc-800 text-zinc-50" : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200")
              }
            >
              <n.icon className="h-4 w-4" strokeWidth={1.75} />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <button
          onClick={() => setPaletteOpen(true)}
          data-testid="open-command-palette"
          className="mx-2 mb-2 flex items-center gap-2 rounded-md border border-zinc-800 px-3 py-2 text-xs text-zinc-500 hover:bg-zinc-900 transition-colors"
        >
          <Search className="h-3.5 w-3.5" /> Search…
          <span className="ml-auto font-mono flex items-center gap-0.5"><Command className="h-3 w-3" />K</span>
        </button>

        <div className="border-t border-zinc-800 p-3 flex items-center gap-2">
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />
          ) : (
            <div className="h-8 w-8 rounded-full bg-zinc-800" />
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm text-zinc-200">{user?.name || user?.email}</div>
            <div className="text-[10px] font-mono uppercase text-zinc-500">{user?.role}</div>
          </div>
          <button onClick={logout} data-testid="logout-btn" className="text-zinc-500 hover:text-rose-400 transition-colors">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </aside>

      <main className="pl-0 lg:pl-60">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 py-8">
          <ConnectionStatus />
          <Outlet />
        </div>
      </main>
    </div>
  );
}
